import {
  Bell,
  Focus,
  Menu,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import ContextSwitcher from "./ContextSwitcher";
import InspectPanel from "@/components/inspect/InspectPanel";
import ChatFilePreviewPanel from "@/components/operate/ChatFilePreviewPanel";
import HistoryDetailPanel from "@/components/operate/HistoryDetailPanel";
import RunContextPanel from "@/components/operate/RunContextPanel";
import { Button } from "@/components/ui/Button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/Sheet";
import { useInspectStore } from "@store/inspect";
import { useCanvasStore } from "@store/canvas";
import { useOperateStore } from "@/store/operate";
import { useShellStore } from "@/store/shell";
import { usePermissions } from "@/hooks/usePermissions";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { cn } from "@/utils/cn";
import { routePosture, type ShellPosture } from "@/utils/posture";

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );
  const [isNarrowScreen, setIsNarrowScreen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  const [mobileContextPanelOpen, setMobileContextPanelOpen] = useState(false);
  const selectedNodeId = useInspectStore((state) => state.selectedNodeId);
  const setSelectedNode = useInspectStore((state) => state.setSelectedNode);
  const nodes = useCanvasStore((state) => state.nodes);
  const executionSteps = useOperateStore((state) => state.executionSteps);
  const executionPanelOpen = useOperateStore(
    (state) => state.executionPanelOpen,
  );
  const setExecutionPanelOpen = useOperateStore(
    (state) => state.setExecutionPanelOpen,
  );
  const currentSessionId = useOperateStore((state) => state.currentSessionId);
  const selectedRecipeId = useOperateStore((state) => state.selectedRecipeId);
  const selectedRecipePreview = useOperateStore(
    (state) => state.selectedRecipePreview,
  );
  const selectedExecution = useOperateStore((state) => state.selectedExecution);
  const historyPanelOpen = useOperateStore((state) => state.historyPanelOpen);
  const setHistoryPanelOpen = useOperateStore(
    (state) => state.setHistoryPanelOpen,
  );
  const chatExecutionPanelTarget = useOperateStore(
    (state) => state.chatExecutionPanelTarget,
  );
  const clearChatExecutionPanelTarget = useOperateStore(
    (state) => state.clearChatExecutionPanelTarget,
  );
  const chatFilePanelTarget = useOperateStore(
    (state) => state.chatFilePanelTarget,
  );
  const clearChatFilePanelTarget = useOperateStore(
    (state) => state.clearChatFilePanelTarget,
  );
  const focusMode = useShellStore((state) => state.focusMode);
  const setFocusMode = useShellStore((state) => state.setFocusMode);
  const toggleFocusMode = useShellStore((state) => state.toggleFocusMode);
  const leftPanelWidth = useShellStore((state) => state.leftPanelWidth);
  const setLeftPanelWidth = useShellStore((state) => state.setLeftPanelWidth);
  const rightPanelVisible = useShellStore((state) => state.rightPanelVisible);
  const rightPanelWidth = useShellStore((state) => state.rightPanelWidth);
  const setRightPanelVisible = useShellStore(
    (state) => state.setRightPanelVisible,
  );
  const setRightPanelWidth = useShellStore((state) => state.setRightPanelWidth);
  const toggleRightPanel = useShellStore((state) => state.toggleRightPanel);
  const rightPanelManuallyToggled = useShellStore(
    (state) => state.rightPanelManuallyToggled,
  );
  const setRightPanelManuallyToggled = useShellStore(
    (state) => state.setRightPanelManuallyToggled,
  );
  const autoShowSuppressed = useShellStore((state) => state.autoShowSuppressed);
  const suppressPanel = useShellStore((state) => state.suppressPanel);
  const clearAutoShowSuppressed = useShellStore(
    (state) => state.clearAutoShowSuppressed,
  );
  const buildSidebarCollapsed = useShellStore(
    (state) => state.buildSidebarCollapsed,
  );
  const { canWrite, canDeploy, canManageUsers } = usePermissions();
  const canBuildRecipes = canWrite("recipes");
  const canBuildDeploy = canDeploy();
  const canBuildExternalControl = canManageUsers();
  const canAccessBuild = canBuildDeploy || canBuildExternalControl;
  const canAccessStudio = canBuildExternalControl || canBuildRecipes;
  const defaultBuildRoute = useMemo(() => {
    if (canBuildDeploy) {
      return "/build/access";
    }
    if (canBuildExternalControl) {
      return "/build/api-keys";
    }
    return "/operate/chat";
  }, [canBuildDeploy, canBuildExternalControl]);
  const defaultStudioRoute = canBuildRecipes
    ? "/studio/chat"
    : canBuildExternalControl
      ? "/studio/agent-control"
      : "/operate/chat";
  // The historical recipe canvas is retained for migration support, but no
  // public route mounts it after the Agent Spec V2 cutover.
  const isBuildCanvasRoute = false;
  const isOperateChatRoute = location.pathname.startsWith("/operate/chat");
  const isOperateCatalogRoute =
    location.pathname.startsWith("/operate/catalog");
  const isOperateHistoryRoute =
    location.pathname.startsWith("/operate/history");
  const posture = routePosture(location.pathname);
  const showInspectPanel = isBuildCanvasRoute && Boolean(selectedNodeId);
  const showChatFilePanel = isOperateChatRoute && Boolean(chatFilePanelTarget);
  const showCatalogRecipePanel =
    isOperateCatalogRoute && Boolean(selectedRecipeId);
  const hasRunContext =
    Boolean(chatExecutionPanelTarget) ||
    Boolean(selectedRecipeId) ||
    executionSteps.length > 0 ||
    executionPanelOpen;
  const showExecutionPanel =
    isOperateChatRoute && hasRunContext && !showChatFilePanel;
  const showHistoryPanel =
    isOperateHistoryRoute && Boolean(selectedExecution) && historyPanelOpen;
  const hasRightPanelContent =
    showInspectPanel ||
    showChatFilePanel ||
    showCatalogRecipePanel ||
    showExecutionPanel ||
    showHistoryPanel;
  const displayRightPanel = isNarrowScreen
    ? mobileContextPanelOpen && hasRightPanelContent
    : focusMode === "full" && rightPanelVisible && hasRightPanelContent;
  const isBuildMode = posture === "build";
  const effectiveSidebarOpen = isNarrowScreen
    ? sidebarOpen
    : focusMode === "full" &&
      sidebarOpen &&
      !(isBuildMode && buildSidebarCollapsed);
  const previousRightPanelContentRef = useRef(hasRightPanelContent);
  const previousPostureRef = useRef(posture);
  const desktopSidebarPreferenceRef = useRef(sidebarOpen);
  const isResizingLeftPanelRef = useRef(false);
  const isResizingRightPanelRef = useRef(false);

  const executionSummary = useMemo(() => {
    const totalDuration = executionSteps.reduce((acc, step) => {
      if (step.completedAt && step.startedAt) {
        return acc + (step.completedAt.getTime() - step.startedAt.getTime());
      }
      return acc;
    }, 0);

    const ratioProgress =
      executionSteps.length === 0
        ? 0
        : (executionSteps.filter((step) => step.status === "completed").length /
            executionSteps.length) *
          100;
    const explicitProgress = executionSteps.reduce((acc, step) => {
      if (typeof step.progress === "number" && Number.isFinite(step.progress)) {
        return Math.max(acc, step.progress);
      }
      return acc;
    }, 0);
    const currentProgress = Math.max(ratioProgress, explicitProgress);

    return { currentProgress, totalDuration };
  }, [executionSteps]);

  useEffect(() => {
    if (!selectedNodeId) {
      setSelectedNode(null);
      return;
    }

    const selectedNode =
      nodes.find((node) => node.id === selectedNodeId)?.data || null;
    setSelectedNode(selectedNode);
  }, [nodes, selectedNodeId, setSelectedNode]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => {
      setIsNarrowScreen(media.matches);
    };

    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isNarrowScreen) {
      desktopSidebarPreferenceRef.current = sidebarOpen;
    }
  }, [isNarrowScreen, sidebarOpen]);

  useEffect(() => {
    if (isNarrowScreen) {
      setSidebarOpen(false);
      setMobileContextPanelOpen(false);
      return;
    }

    setSidebarOpen(desktopSidebarPreferenceRef.current);
  }, [isNarrowScreen]);

  useEffect(() => {
    if (!hasRightPanelContent) {
      setMobileContextPanelOpen(false);
      // Reset manual toggle flag when all content disappears
      setRightPanelManuallyToggled(false);
    }
  }, [hasRightPanelContent, setRightPanelManuallyToggled]);

  // Close the right panel when the posture changes (Build ↔ Operate) so that
  // panels like Calisto / MetaRecette don't bleed into the other mode.
  // Auto-show suppression persists until the user navigates within the same
  // posture (e.g. Chat → Catalog) or manually toggles the panel.
  useEffect(() => {
    if (previousPostureRef.current !== posture) {
      if (posture === "calisto") {
        clearAutoShowSuppressed();
        setRightPanelVisible(true);
      } else {
        suppressPanel();
      }
      previousPostureRef.current = posture;
    }
  }, [clearAutoShowSuppressed, posture, setRightPanelVisible, suppressPanel]);

  // Clear auto-show suppression when the user navigates within the same posture.
  // We track pathname changes — if posture stays the same, it means in-mode navigation.
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      const prevPosture = routePosture(previousPathnameRef.current);
      const currPosture = routePosture(location.pathname);
      if (prevPosture === currPosture && autoShowSuppressed) {
        clearAutoShowSuppressed();
      }
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, autoShowSuppressed, clearAutoShowSuppressed]);

  useEffect(() => {
    // Suppress auto-show during posture transitions — the boolean stays true
    // until the user navigates within the same posture or manually toggles.
    if (autoShowSuppressed) {
      previousRightPanelContentRef.current = hasRightPanelContent;
      return;
    }
    // Auto-show panel when content appears
    if (!previousRightPanelContentRef.current && hasRightPanelContent) {
      setRightPanelVisible(true);
    }
    // Auto-hide panel when content disappears (unless user manually toggled it)
    if (
      previousRightPanelContentRef.current &&
      !hasRightPanelContent &&
      !rightPanelManuallyToggled
    ) {
      setRightPanelVisible(false);
    }
    previousRightPanelContentRef.current = hasRightPanelContent;
  }, [
    autoShowSuppressed,
    hasRightPanelContent,
    rightPanelManuallyToggled,
    setRightPanelVisible,
  ]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (isResizingLeftPanelRef.current) {
        setLeftPanelWidth(event.clientX);
        return;
      }
      if (!isResizingRightPanelRef.current) return;
      const nextWidth = window.innerWidth - event.clientX;
      setRightPanelWidth(nextWidth);
    };

    const stopResize = () => {
      const wasResizingLeft = isResizingLeftPanelRef.current;
      const wasResizingRight = isResizingRightPanelRef.current;
      if (!wasResizingLeft && !wasResizingRight) return;
      isResizingLeftPanelRef.current = false;
      isResizingRightPanelRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
    };
  }, [setLeftPanelWidth, setRightPanelWidth]);

  const startLeftPanelResize = () => {
    isResizingLeftPanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const startRightPanelResize = () => {
    isResizingRightPanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useKeyboardShortcuts({
    shortcuts: [
      {
        key: ".",
        ctrl: true,
        shift: true,
        callback: () => {
          toggleFocusMode();
        },
      },
      {
        key: "\\",
        ctrl: true,
        shift: true,
        callback: () => {
          if (hasRightPanelContent) {
            toggleRightPanel();
          }
        },
      },
      {
        key: "Escape",
        callback: () => {
          if (focusMode === "center") {
            setFocusMode("full");
          }
        },
      },
    ],
  });

  const handlePostureChange = (nextPosture: ShellPosture) => {
    if (nextPosture === posture) {
      return;
    }

    // Suppress the panel for a short window to prevent auto-show from
    // immediately re-opening it during post-navigation renders.
    suppressPanel();

    if (nextPosture === "build") {
      navigate(defaultBuildRoute);
      return;
    }

    if (nextPosture === "calisto") {
      navigate(defaultStudioRoute);
      return;
    }

    navigate("/operate/chat");
  };

  const renderRightPanelContent = () => {
    if (showInspectPanel) {
      return (
        <div className="flex h-full flex-col overflow-hidden">
          <div className="border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold">{t("shell.inspectTitle")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("shell.inspectDescription")}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <InspectPanel />
          </div>
        </div>
      );
    }

    if (showChatFilePanel && chatFilePanelTarget) {
      return (
        <ChatFilePreviewPanel
          file={chatFilePanelTarget}
          onClose={clearChatFilePanelTarget}
        />
      );
    }

    if (showCatalogRecipePanel) {
      return (
        <RunContextPanel
          key={`catalog-context:${selectedRecipeId ?? "none"}`}
          sessionId={null}
          recipeId={selectedRecipeId}
          recipePreview={selectedRecipePreview}
          executionSteps={[]}
          executionPanelOpen={false}
          onToggleExecution={() => undefined}
          totalDuration={0}
          currentProgress={0}
          showExecutionPanel={false}
        />
      );
    }

    if (showExecutionPanel) {
      return (
        <RunContextPanel
          key={`run-context:${selectedRecipeId ?? "none"}:${
            chatExecutionPanelTarget?.executionId ?? "none"
          }`}
          sessionId={currentSessionId!}
          recipeId={selectedRecipeId}
          recipePreview={selectedRecipePreview}
          executionSteps={executionSteps}
          executionPanelOpen={executionPanelOpen}
          onToggleExecution={() => setExecutionPanelOpen(!executionPanelOpen)}
          executionDetailTarget={chatExecutionPanelTarget}
          onCloseExecutionDetail={clearChatExecutionPanelTarget}
          totalDuration={executionSummary.totalDuration}
          currentProgress={executionSummary.currentProgress}
        />
      );
    }

    if (selectedExecution) {
      return (
        <div className="flex h-full flex-col overflow-hidden">
          <div className="border-b border-border px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  {t("shell.executionDetailTitle")}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t("shell.executionDetailDescription")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryPanelOpen(false)}
              >
                {t("common.hide")}
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <HistoryDetailPanel execution={selectedExecution} />
          </div>
        </div>
      );
    }

    return null;
  };

  const postureSwitcher = (
    <div className="rounded-full border border-border/80 bg-card/96 p-1.5 shadow-[0_14px_34px_rgba(15,23,42,0.1)] backdrop-blur-sm dark:bg-card/84 dark:shadow-[0_14px_34px_rgba(2,6,23,0.42)]">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handlePostureChange("operate")}
          aria-current={posture === "operate" ? "page" : undefined}
          className={cn(
            "rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200",
            posture === "operate"
              ? "bg-cyan-600 text-white shadow-md shadow-cyan-600/30 dark:bg-cyan-500 dark:shadow-cyan-500/25"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
          )}
        >
          {t("common.run")}
        </button>
        <button
          type="button"
          onClick={() => handlePostureChange("calisto")}
          disabled={!canAccessStudio}
          aria-current={posture === "calisto" ? "page" : undefined}
          className={cn(
            "rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200",
            posture === "calisto"
              ? "bg-agent-indigo text-white shadow-md shadow-agent-indigo/25"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          {t("common.studio")}
        </button>
        <button
          type="button"
          onClick={() => handlePostureChange("build")}
          disabled={!canAccessBuild}
          aria-current={posture === "build" ? "page" : undefined}
          className={cn(
            "rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200",
            posture === "build"
              ? "bg-violet-600 text-white shadow-md shadow-violet-600/30 dark:bg-violet-500 dark:shadow-violet-500/25"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
          )}
        >
          {t("common.build")}
        </button>
      </div>
    </div>
  );
  const deploymentScopeControls = (
    <div
      className={cn(
        "flex min-w-0 items-center gap-3",
        isNarrowScreen ? "flex-1" : "shrink-0",
      )}
    >
      <div className="min-w-0">
        <ContextSwitcher />
      </div>
    </div>
  );
  const topbarActions = (
    <div className="flex min-w-0 items-center justify-end gap-3">
      <div className="flex min-w-0 items-center gap-2">
        {!isNarrowScreen && (
          <Button
            variant={focusMode === "center" ? "default" : "outline"}
            size="sm"
            className="gap-2 px-3"
            onClick={toggleFocusMode}
            aria-label={t("shell.toggleCenterFocus")}
            title={t("shell.focusCenterContent")}
          >
            <Focus className="h-4 w-4" />
            <span className="hidden 2xl:inline">
              {focusMode === "center" ? t("common.focus") : t("common.full")}
            </span>
          </Button>
        )}
        <Button
          variant={displayRightPanel ? "default" : "outline"}
          size="sm"
          className="gap-2 px-3"
          onClick={() => {
            if (isNarrowScreen) {
              setMobileContextPanelOpen((current) => !current);
              return;
            }
            toggleRightPanel();
          }}
          disabled={
            isNarrowScreen
              ? !hasRightPanelContent
              : focusMode !== "full" ||
                !hasRightPanelContent
          }
          aria-label={t("shell.toggleContextPanel")}
          title={t("shell.toggleRightPanel")}
        >
          {displayRightPanel ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <PanelRightOpen className="h-4 w-4" />
          )}
          {!isNarrowScreen && (
            <span className="hidden 2xl:inline">{t("common.context")}</span>
          )}
        </Button>
      </div>

      {!isNarrowScreen && (
        <div className="flex items-center gap-2 border-l border-border/70 pl-3">
          <Button
            variant="outline"
            size="icon"
            aria-label={t("common.notifications")}
          >
            <Bell className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {isNarrowScreen && effectiveSidebarOpen && (
        <button
          type="button"
          aria-label={t("common.close")}
          className="fixed inset-0 z-30 bg-black/35 backdrop-blur-[1px] lg:hidden"
          onClick={() => {
            setSidebarOpen(false);
            setMobileContextPanelOpen(false);
          }}
        />
      )}

      <Sidebar
        open={effectiveSidebarOpen}
        width={leftPanelWidth}
        mobile={isNarrowScreen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {effectiveSidebarOpen && !isNarrowScreen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("shell.resizeNavigationPanel")}
          className="group relative hidden w-1 cursor-col-resize bg-transparent lg:block"
          onPointerDown={startLeftPanelResize}
        >
          <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary/50" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className={cn(
            "relative z-20 min-h-16 border-b border-border bg-background/92 px-3 py-2 backdrop-blur-md sm:px-4",
            isNarrowScreen
              ? "flex flex-wrap items-center justify-between gap-3"
              : "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4",
          )}
        >
          {isNarrowScreen ? (
            <>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSidebarOpen(true)}
                  aria-label={t("shell.resizeNavigationPanel")}
                >
                  <Menu className="h-4 w-4" />
                </Button>
                {deploymentScopeControls}
              </div>

              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                {postureSwitcher}
                {topbarActions}
              </div>
            </>
          ) : (
            <>
              <div className="min-w-0 justify-self-start">
                {deploymentScopeControls}
              </div>
              <div className="justify-self-center">{postureSwitcher}</div>
              <div className="min-w-0 justify-self-end">{topbarActions}</div>
            </>
          )}
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="min-w-0 flex-1 overflow-auto">{children}</div>

          {displayRightPanel && (
            <>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t("shell.resizeContextPanel")}
                className="group relative hidden w-1 cursor-col-resize bg-transparent lg:block"
                onPointerDown={startRightPanelResize}
              >
                <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary/50" />
              </div>
            </>
          )}

          {displayRightPanel && !isNarrowScreen && (
            <>
              <aside
                className="border-l border-border bg-card"
                style={{ width: `${rightPanelWidth}px` }}
              >
                {renderRightPanelContent()}
              </aside>
            </>
          )}
        </div>
      </div>

      {isNarrowScreen && hasRightPanelContent && (
        <Sheet
          open={displayRightPanel}
          onOpenChange={setMobileContextPanelOpen}
        >
          <SheetContent closeLabel={t("common.close")}>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <SheetTitle className="text-sm font-semibold">
                {t("common.context")}
              </SheetTitle>
              <SheetDescription className="text-xs text-muted-foreground">
                {t("shell.emptyContextDescription")}
              </SheetDescription>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {renderRightPanelContent()}
          </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
